import { inject, makeStateKey, PLATFORM_ID, TransferState } from '@angular/core';

import { ResolveFn } from '@angular/router';

import { HttpClient } from '@angular/common/http';

import { map, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { isPlatformServer } from '@angular/common';
import { getServerCache, setServerCache, markRefreshing } from './server-data-cache';

export const SHOP_DATA_KEY = makeStateKey<any>('shopData');
const SERVER_CACHE_KEY = 'resolver:categories-resume';

/** Number of products to transfer to the client via TransferState (above-fold) */
const TRANSFER_PRODUCT_LIMIT = 12;

export const productsResolver: ResolveFn<any> = (route, state) => {
    const http = inject(HttpClient);
    const transferState = inject(TransferState);
    const platformId = inject(PLATFORM_ID);

    const api = environment.apis.v3;

    // Client-side: use transferred data if available
    const existingData = transferState.get(SHOP_DATA_KEY, null);
    if (existingData) {
        transferState.remove(SHOP_DATA_KEY);
        return of(existingData);
    }

    // SSR: check server-side data cache (stale-while-revalidate)
    if (isPlatformServer(platformId)) {
        const cached = getServerCache<any>(SERVER_CACHE_KEY);
        if (cached.data) {
            const slim = slimTransferData(cached.data);
            transferState.set(SHOP_DATA_KEY, slim);

            if (cached.stale && markRefreshing(SERVER_CACHE_KEY)) {
                http.get(`${api}/categories-resume`, { transferCache: false }).subscribe({
                    next: data => setServerCache(SERVER_CACHE_KEY, data),
                    error: () => { /* non-fatal: next request will retry */ }
                });
            }

            // Hand the component the SAME slim payload we transfer to the client, so
            // SSR only renders/maps the above-fold slice (not the full catalog) and
            // server/client hydrate from identical data. Full catalog is fetched
            // client-side via fetchFullCatalog() (_isPartial flag, browser-guarded).
            return of(slim);
        }
    }

    return http.get(`${api}/categories-resume`, { transferCache: false }).pipe(
        map((data: any) => {
            if (isPlatformServer(platformId)) {
                setServerCache(SERVER_CACHE_KEY, data);
                const slim = slimTransferData(data);
                transferState.set(SHOP_DATA_KEY, slim);
                return slim; // server renders the slim above-fold slice only
            }
            return data; // cold client fetch (no TransferState): full catalog is fine off the server
        })
    );
};

/**
 * Trim the resolver data to the first N products (+ sidebar) for both SSR render
 * and TransferState. Keeping the server on this slim slice is what bounds per-request
 * memory: the grid only iterates/maps ~N products instead of the whole catalog.
 * `_isPartial` tells the shop component to fetch the full catalog client-side.
 * `productUrlIndex` preserves every product URL (cheap string map) so the client
 * still has the full routing/SEO index before the full catalog arrives.
 */
function slimTransferData(response: any): any {
    const data = response?.data;
    if (!data?.products) return response;

    return {
        ...response,
        data: {
            ...data,
            products: data.products.slice(0, TRANSFER_PRODUCT_LIMIT),
            productUrlIndex: data.products.map((p: any) => p.productUrl),
            _isPartial: true
        }
    };
}
